/// <reference path="../types/three-jsx-elements.d.ts" />
'use client';

import React, { useRef, useState, useEffect, useMemo, Suspense, forwardRef } from 'react';
import { Canvas, useFrame, ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Html, Text, Line as DreiLine, useTexture, Sphere, SpotLight } from '@react-three/drei';
import * as THREE from 'three';
import styles from './SkillsGlobe.module.css';
import { ErrorBoundary } from 'react-error-boundary';
import { 
  Group, 
  Mesh, 
  AmbientLight,
  PointLight, 
  SphereGeometry, 
  MeshStandardMaterial,
  PlaneGeometry,
  MeshBasicMaterial,
  BufferGeometry,
  BufferAttribute,
  LineBasicMaterial,
  Points,
  PointsMaterial
} from '../utils/ThreeElements';

export interface Skill {
  id: string;
  name: string;
  category: string;
  level: number;
  position?: [number, number, number];
  description?: string;
  icon?: string; // emoji or SVG
}

export interface Project {
  id: number;
  title: string;
  description: string;
  technologies: string[];
  github: string | null;
  demo: string | null;
  image: string | null;
}

// Utility: Generate spherical distribution of points
const generateSphericalPositions = (count: number, radius: number): [number, number, number][] => {
  const positions: [number, number, number][] = [];
  const phi = Math.PI * (3 - Math.sqrt(5)); // golden angle
  
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2; // y goes from 1 to -1
    const radius_at_y = Math.sqrt(1 - y * y); // radius at y
    
    const theta = phi * i; // golden angle increment
    
    const x = Math.cos(theta) * radius_at_y;
    const z = Math.sin(theta) * radius_at_y;
    
    positions.push([x * radius, y * radius, z * radius]);
  }
  
  return positions;
};

// Create skill icons mapping
const skillIconMap: Record<string, string> = {
  "Python": "🐍",
  "JavaScript": "📜",
  "Java": "☕",
  "C++": "🔧",
  "C": "🧩",
  "React": "⚛️",
  "Next.js": "▲",
  "HTML5": "🧱",
  "CSS3": "🎨",
  "Node.js": "🟢",
  "MongoDB": "🍃",
  "Firebase": "🔥",
  "Supabase": "⚡",
  "Clerk": "🔑",
  "SQL": "💾",
  "Docker": "🐳",
  "Git": "📂",
  "Machine Learning": "🧠",
  "Data Analysis": "📊",
  "NLP": "💬",
  "Big Data Tools": "📚",
  "Cloud Computing": "☁️"
};

interface SkillsGlobeProps {
  skills: Skill[];
  projects?: Project[];
}

// Main component
const SkillsGlobe = ({ skills, projects = [] }: SkillsGlobeProps) => {
  // Prepare skills with positions
  const skillsWithPositions = React.useMemo(() => {
    const positions = generateSphericalPositions(skills.length, 2.5);
    return skills.map((skill, i) => ({
      ...skill,
      position: skill.position || positions[i],
      icon: skillIconMap[skill.name] || "⚡"
    }));
  }, [skills]);

  const [hoveredSkill, setHoveredSkill] = useState<Skill | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [relatedProjects, setRelatedProjects] = useState<Project[]>([]);
  
  // Find related projects when a skill is selected
  useEffect(() => {
    if (selectedSkill && projects.length > 0) {
      const related = projects.filter(project => 
        project.technologies.some(tech => 
          tech.toLowerCase() === selectedSkill.name.toLowerCase()
        )
      );
      setRelatedProjects(related);
    } else {
      setRelatedProjects([]);
    }
  }, [selectedSkill, projects]);

  // Handler for skill click
  const handleSkillClick = (skill: Skill) => {
    setSelectedSkill(prev => prev?.id === skill.id ? null : skill);
    setAutoRotate(false);
  };

  // Reset selection
  const handleCloseModal = () => {
    setSelectedSkill(null);
    setAutoRotate(true);
  };

  const CanvasWrapper = () => (
    <Canvas camera={{ position: [0, 0, 6], fov: 60 }}>
      <AmbientLight intensity={0.6} />
      <PointLight position={[10, 10, 10]} intensity={1.2} />
      
      {/* OrbitControls with auto-rotation */}
      <OrbitControls 
        enableZoom={true} 
        enablePan={false} 
        autoRotate={autoRotate} 
        autoRotateSpeed={0.5}
        minDistance={4}
        maxDistance={10}
        target={[0, 0, 0]}
      />

      <RotatingGlobe />
      
      {/* Skills spheres */}
      {skillsWithPositions.map((skill) => (
        <SkillSphere 
          key={skill.id} 
          skill={skill} 
          onHover={setHoveredSkill} 
          onClick={handleSkillClick}
          isSelected={selectedSkill?.id === skill.id}
        />
      ))}

      {/* Network connections */}
      <NetworkConnections skills={skillsWithPositions} />

      {/* Matrix particles */}
      <MatrixParticles />

      {/* Radar pulse */}
      <RadarPulse />

      {/* Hovering tooltip */}
      {hoveredSkill && !selectedSkill && (
        <Html position={[0, 3, 0]} center>
          <div className={styles.skillTooltip}>
            <div className={styles.skillTooltipName}>{hoveredSkill.name}</div>
            <div className={styles.skillTooltipLevel}>
              Level: {hoveredSkill.level}%
            </div>
            <div className={styles.skillTooltipHint}>
              Click to {hoveredSkill.name.toLowerCase().includes('react') || 
                 hoveredSkill.name.toLowerCase().includes('python') || 
                 hoveredSkill.name.toLowerCase().includes('javascript') ? 'see related projects' : 'view details'}
            </div>
          </div>
        </Html>
      )}
    </Canvas>
  );

  return (
    <div className={styles.skillsGlobeContainer}>
      <ErrorBoundary fallback={<div className={styles.errorMessage}>Something went wrong with the 3D globe. Please refresh.</div>}>
        <Suspense fallback={<div className={styles.loadingMessage}>Loading 3D Globe...</div>}>
          <CanvasWrapper />
        </Suspense>
      </ErrorBoundary>

      {/* Skill details modal with improved mobile layout */}
      {selectedSkill && (
        <div className={styles.skillModalOverlay} onClick={handleCloseModal}>
          <div className={styles.skillModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={handleCloseModal} aria-label="Close">×</button>
            <h2>{selectedSkill.name}</h2>
            
            <div className={styles.skillLevelIndicator}>
              <div className={styles.skillLevelLabel}>Proficiency:</div>
              <div className={styles.skillLevelBar}>
                <div className={styles.skillLevelFill} style={{ width: `${selectedSkill.level}%` }}></div>
              </div>
              <div className={styles.skillLevelPercentage}>{selectedSkill.level}%</div>
            </div>
            
            <div className={styles.skillCategory}>
              Category: <span>{selectedSkill.category}</span>
            </div>
            
            {relatedProjects.length > 0 ? (
              <div className={styles.relatedProjects}>
                <h3>Related Projects</h3>
                <div className={styles.projectsList}>
                  {relatedProjects.map(project => (
                    <div key={project.id} className={styles.relatedProject}>
                      <h4>{project.title}</h4>
                      <p>{project.description}</p>
                      <div className={styles.projectLinks}>
                        {project.github && (
                          <a href={project.github} target="_blank" rel="noopener noreferrer" className={styles.projectLink}>
                            GitHub
                          </a>
                        )}
                        {project.demo && (
                          <a href={project.demo} target="_blank" rel="noopener noreferrer" className={styles.projectLink}>
                            Live Demo
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.noProjects}>
                <p>No related projects found for this skill.</p>
                <p>Feel free to check out my other projects in the Projects section!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// A rotating globe in the center with grid lines
const RotatingGlobe = () => {
  const globeRef = useRef<THREE.Group>(null);
  
  useFrame(({ clock }) => {
    if (globeRef.current) {
      globeRef.current.rotation.y = clock.getElapsedTime() * 0.1;
    }
  });
  
  return (
    <Group ref={globeRef}>
      <Mesh>
        <SphereGeometry args={[2, 32, 32]} />
        <MeshStandardMaterial 
          color="#001500" 
          wireframe={true} 
          emissive="#003300"
          emissiveIntensity={0.5}
          transparent
          opacity={0.3}
        />
      </Mesh>
    </Group>
  );
};

// Individual skill sphere component
const SkillSphere = ({
  skill,
  onHover,
  onClick,
  isSelected,
}: {
  skill: Skill;
  onHover: (s: Skill | null) => void;
  onClick: (s: Skill) => void;
  isSelected: boolean;
}) => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (!meshRef.current) return;
    
    // Pulse effect for hovered/selected skills
    if (hovered || isSelected) {
      const scale = 1 + Math.sin(state.clock.getElapsedTime() * 5) * 0.1;
      meshRef.current.scale.setScalar(scale);
    } else {
      // Subtle floating animation for non-selected skills
      const scale = 1 + Math.sin(state.clock.getElapsedTime() * 2 + parseInt(skill.id)) * 0.05;
      meshRef.current.scale.setScalar(scale);
    }
  });

  const handlePointerOver = () => {
    setHovered(true);
    onHover(skill);
  };

  const handlePointerOut = () => {
    setHovered(false);
    onHover(null);
  };

  const handleClick = () => {
    onClick(skill);
    
    // Emit an event to highlight projects related to this skill
    const event = new CustomEvent('highlightProject', {
      detail: { skillName: skill.name }
    });
    window.dispatchEvent(event);
  };

  // Choose color based on skill category
  const getSkillColor = (category: string): string => {
    const colors: Record<string, string> = {
      'languages': '#00a2ff', // Blue
      'frontend': '#88ff00', // Light green
      'backend': '#00dfff', // Cyan
      'database': '#ff8800', // Orange
      'devops': '#ff00ff', // Pink
      'tools': '#ffff00', // Yellow
      'ai': '#ff0088'  // Pink-red
    };
    return colors[category] || '#00a2ff';
  };

  const skillColor = getSkillColor(skill.category);

  return (
    <Group position={skill.position}>
      <Mesh
        ref={meshRef}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      >
        <SphereGeometry args={[0.3, 32, 32]} />
        <MeshStandardMaterial 
          color={skillColor}
          emissive={skillColor}
          emissiveIntensity={isSelected ? 1.5 : hovered ? 1.0 : 0.5}
        />
      </Mesh>

      <Html center>
        <div 
          className={styles.skillIcon} 
          style={{ 
            fontSize: isSelected ? '2em' : hovered ? '1.5em' : '1.2em',
            textShadow: `0 0 10px ${skillColor}`
          }}
        >
          {skill.icon}
        </div>
      </Html>
    </Group>
  );
};

// Network connections for cyberpunk effect
const NetworkConnections = ({ skills }: { skills: Skill[] }) => {
  const connectionLines = useMemo(() => {
    const lines: Array<{ points: [number, number, number][] }> = [];
    
    // Connect skills that are in related categories
    const categoryMap: Record<string, Skill[]> = {};
    
    // Group skills by category
    skills.forEach(skill => {
      if (!categoryMap[skill.category]) {
        categoryMap[skill.category] = [];
      }
      categoryMap[skill.category].push(skill);
    });
    
    // Connect skills within the same category
    Object.values(categoryMap).forEach(categorySkills => {
      if (categorySkills.length > 1) {
        for (let i = 0; i < categorySkills.length; i++) {
          for (let j = i + 1; j < categorySkills.length; j++) {
            if (Math.random() > 0.5) continue; // Only connect some skills for cleaner look, increased connection density
            
            const skillA = categorySkills[i];
            const skillB = categorySkills[j];
            
            if (skillA.position && skillB.position) {
              lines.push({
                points: [skillA.position, skillB.position]
              });
            }
          }
        }
      }
    });
    
    return lines;
  }, [skills]);
  
  return (
    <Group>
      {connectionLines.map((line, i) => (
        <NetworkLine key={i} points={line.points} />
      ))}
    </Group>
  );
};

// A single network connection line with cyberpunk animation
const NetworkLine = ({ points }: { points: [number, number, number][] }) => {
  const lineRef = useRef<THREE.Mesh>(null);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    // Animate line drawing on first render
    let interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 1) {
          clearInterval(interval);
          return 1;
        }
        return prev + 0.05;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, []);
  
  useFrame(({ clock }) => {
    if (lineRef.current && progress === 1) {
      // Enhanced pulse effect along the line with brighter colors
      const material = lineRef.current.material as THREE.LineBasicMaterial;
      const pulseSpeed = 1.5;
      const t = clock.getElapsedTime() * pulseSpeed;
      
      // Bright neon colors for enhanced visibility
      const colorA = new THREE.Color("#00dfff"); // Bright cyan
      const colorB = new THREE.Color("#0088ff"); // Bright blue
      const mixRatio = (Math.sin(t) + 1) / 2;
      
      material.color.copy(colorA).lerp(colorB, mixRatio);
      material.opacity = 0.8 + 0.2 * Math.sin(t * 2) ** 2; // Higher base opacity
      
      if (lineRef.current.userData.pulseBrightness === undefined) {
        lineRef.current.userData.pulseBrightness = true;
      }
    }
  });
  
  // Calculate points for the animated growing line
  const currentPoints = useMemo(() => {
    if (progress === 0) return [points[0], points[0]];
    if (progress === 1) return points;
    
    const start = points[0];
    const end = points[1];
    const currentEnd: [number, number, number] = [
      start[0] + (end[0] - start[0]) * progress,
      start[1] + (end[1] - start[1]) * progress,
      start[2] + (end[2] - start[2]) * progress
    ];
    
    return [start, currentEnd];
  }, [points, progress]);
  
  return (
    <Group>
      {/* Create custom lines instead of using drei Line */}
      <Mesh>
        <BufferGeometry>
          <BufferAttribute
            attach="attributes-position"
            array={new Float32Array(currentPoints.flat())}
            count={currentPoints.length}
            itemSize={3}
          />
        </BufferGeometry>
        <LineBasicMaterial
          color="#00dfff"
          transparent
          opacity={0.4}
          linewidth={4}
        />
      </Mesh>
      
      {/* Main bright line */}
      <Mesh ref={lineRef}>
        <BufferGeometry>
          <BufferAttribute
            attach="attributes-position"
            array={new Float32Array(currentPoints.flat())}
            count={currentPoints.length}
            itemSize={3}
          />
        </BufferGeometry>
        <LineBasicMaterial
          color="#00a2ff"
          transparent
          opacity={0.9}
          linewidth={2}
        />
      </Mesh>
      
      {/* Add small spheres at connection points for better visibility */}
      {currentPoints.map((point, i) => (
        <Mesh key={i} position={point} scale={0.08}>
          <SphereGeometry args={[1, 16, 16]} />
          <MeshStandardMaterial 
            color="#00dfff"
            emissive="#00dfff"
            emissiveIntensity={2}
          />
        </Mesh>
      ))}
    </Group>
  );
};

// Matrix Code Particles for hacker aesthetic
const MatrixParticles = () => {
  const count = 250; // Number of particles
  const particlesRef = useRef<THREE.Points>(null);
  
  // Generate random binary particles
  const [particles] = useState(() => {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      // Position in a sphere around the skills globe
      const radius = 4 + Math.random() * 4; // 4-8 units away from center
      const theta = Math.random() * Math.PI * 2; // Random angle
      const phi = Math.acos(2 * Math.random() - 1); // Random height
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      
      // Random size for each particle
      sizes[i] = 0.05 + Math.random() * 0.1;
      
      // Green color with slight variations
      colors[i * 3] = 0; // R
      colors[i * 3 + 1] = 0.8 + Math.random() * 0.2; // G 
      colors[i * 3 + 2] = 0; // B
    }
    
    return { positions, sizes, colors };
  });
  
  useFrame(({ clock }) => {
    if (particlesRef.current) {
      const time = clock.getElapsedTime();
      const geometry = particlesRef.current.geometry;
      const positionAttribute = geometry.getAttribute('position');
      
      // Update particle positions for flowing effect
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        
        // Add slight movement along a sine wave
        positionAttribute.array[i3 + 1] -= 0.01 * (1 + Math.sin(time + i * 0.1)); // Move downward
        
        // If particle goes too far down, reset to top
        if (positionAttribute.array[i3 + 1] < -6) {
          positionAttribute.array[i3 + 1] = 6;
        }
      }
      
      positionAttribute.needsUpdate = true;
    }
  });
  
  return (
    <Points ref={particlesRef}>
      <BufferGeometry>
        <BufferAttribute
          attach="attributes-position"
          count={count}
          array={particles.positions}
          itemSize={3}
        />
        <BufferAttribute
          attach="attributes-size"
          count={count}
          array={particles.sizes}
          itemSize={1}
        />
        <BufferAttribute
          attach="attributes-color"
          count={count}
          array={particles.colors}
          itemSize={3}
        />
      </BufferGeometry>
      <PointsMaterial
        size={0.1}
        vertexColors
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </Points>
  );
};

// Radar Pulse Animation (circular waves emanating from center)
const RadarPulse = () => {
  const groupRef = useRef<THREE.Group>(null);
  const [pulses, setPulses] = useState<Array<{ id: number; scale: number; opacity: number }>>([]);
  const nextPulseId = useRef(0);
  
  // Add new pulse every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Use timestamp + incremental ID to ensure unique keys
      const uniqueId = Date.now() * 1000 + nextPulseId.current;
      setPulses(current => [...current, { id: uniqueId, scale: 0.1, opacity: 1 }]);
      nextPulseId.current += 1;
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  useFrame(() => {
    // Update existing pulses
    setPulses(current => 
      current
        .map(pulse => ({
          ...pulse,
          scale: pulse.scale + 0.03, // Grow larger
          opacity: pulse.opacity - 0.01 // Fade out
        }))
        .filter(pulse => pulse.opacity > 0) // Remove completely faded pulses
    );
  });
  
  return (
    <Group ref={groupRef} rotation={[Math.PI / 2, 0, 0]}>
      {pulses.map(pulse => (
        <Mesh key={`pulse-${pulse.id}`} scale={pulse.scale} position={[0, 0, 0]}>
          <PlaneGeometry args={[5, 5]} />
          <MeshBasicMaterial 
            color="#00ff00"
            transparent
            opacity={pulse.opacity}
            side={THREE.DoubleSide}
            wireframe
          />
        </Mesh>
      ))}
    </Group>
  );
};

export default SkillsGlobe;
